import React, { useState } from 'react';
import { Mail, MapPin, Phone, Send, Twitter, Instagram, Facebook } from 'lucide-react';
import { createContactMessage } from '../services/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await createContactMessage(formData);
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pb-24 pt-8 page-padding" data-testid="contact-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
        <p className="text-text-secondary mb-12 max-w-2xl">
          Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
        </p>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="space-y-2">
                <Label htmlFor="name">Your Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  className="input-minimal bg-surface"
                  data-testid="contact-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  className="input-minimal bg-surface"
                  data-testid="contact-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Select 
                  value={formData.subject} 
                  onValueChange={(value) => setFormData({ ...formData, subject: value })}
                >
                  <SelectTrigger className="bg-surface border-border" data-testid="contact-subject">
                    <SelectValue placeholder="Select a subject" />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-border">
                    <SelectItem value="general">General Inquiry</SelectItem>
                    <SelectItem value="subscription">Subscription Support</SelectItem>
                    <SelectItem value="fan_art_request">Fan Art Request</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="How can we help you?"
                  className="bg-surface border-border min-h-[150px]"
                  data-testid="contact-message"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-neo w-full flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="contact-submit"
              >
                <Send size={18} />
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div className="card-hover p-6">
              <h3 className="font-bold text-lg mb-4">Get in Touch</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <Mail className="text-primary flex-shrink-0" size={24} />
                  <div>
                    <p className="font-medium">Email</p>
                    <a href="mailto:support@artifycomics.com" className="text-text-secondary hover:text-primary">
                      support@artifycomics.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Phone className="text-primary flex-shrink-0" size={24} />
                  <div>
                    <p className="font-medium">Phone</p>
                    <p className="text-text-secondary">+1 (555) 123-4567</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <MapPin className="text-primary flex-shrink-0" size={24} />
                  <div>
                    <p className="font-medium">Address</p>
                    <p className="text-text-secondary">
                      123 Comic Street<br />
                      San Francisco, CA 94102
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="card-hover p-6">
              <h3 className="font-bold text-lg mb-4">Follow Us</h3>
              <div className="flex gap-4">
                <a href="#" className="p-3 border border-border hover:border-primary hover:text-primary transition-colors">
                  <Twitter size={24} />
                </a>
                <a href="#" className="p-3 border border-border hover:border-primary hover:text-primary transition-colors">
                  <Instagram size={24} />
                </a>
                <a href="#" className="p-3 border border-border hover:border-primary hover:text-primary transition-colors">
                  <Facebook size={24} />
                </a>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="card-hover h-64 flex items-center justify-center">
              <div className="text-center text-text-secondary">
                <MapPin size={48} className="mx-auto mb-2" />
                <p>Google Maps Integration</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
